import { cva, type VariantProps } from 'class-variance-authority'

/**
 * The card surface every panel sits on, kept out of SectionCard.tsx so that file exports only a
 * component (the Fast Refresh rule), and so a card that cannot be a `SectionCard` — a grid `<li>`,
 * a busy region, a card that draws its own header — paints the same chrome and the same inset.
 *
 * The inset is a pair of variables rather than `p-*` classes because two things have to agree on
 * it: the card's own padding, and a body that runs to the card's edge (a hover-filled row, a ruled
 * footer). Such a body bleeds with `-mx-(--section-card-pad-x)` (and `-my-`/`-mb-` on
 * `--section-card-pad-y`) and pads back with the same, so the phone step-down below can never
 * leave it hanging past the border, which a hard-coded `-mx-6` against a 16px card would. Each
 * card sets both on itself, so a card inside a `flat` section (inset 0) still pads.
 *
 * 20px across and 16px down (the pages revamp's one panel, 2026-10-27), the sides stepping down to
 * 16px when the card's container is narrower than `@sm` (24rem): a 375px phone's main column is
 * 351px. `@max-sm` rather than `px-4 @sm:px-5` on purpose — a card with no container above it
 * (the market rail) matches neither query and keeps the full 20px, instead of reading as a phone.
 */
const INSET =
  'px-(--section-card-pad-x) py-(--section-card-pad-y) [--section-card-pad-x:var(--space-5)] [--section-card-pad-y:var(--space-4)] @max-sm:[--section-card-pad-x:var(--space-4)]'

/**
 * The filled card without its inset, for a block that pads itself (EmptyState's framed states).
 *
 * Borderless in dark, where #111 on the #000 page is edge enough (the 2026-09-28 design refresh
 * draws data cards with no hairline). Light keeps the hairline: the card is white on the #f6f6f4
 * canvas, a step too faint to be an edge on its own. The border stays 1px in both themes, only
 * transparent in dark, so a card is the same size whichever theme draws it.
 */
export const CARD_SURFACE = 'rounded-lg border border-border-subtle bg-card dark:border-transparent'

/**
 * The hairline card without its inset: the `outline` treatment, as a string for a card that
 * paints its own chrome. Its border shows in both themes, unlike `CARD_SURFACE`'s, so pick the
 * one that matches the SectionCard variant the card stands beside.
 */
export const OUTLINE_SURFACE = 'rounded-lg border border-border-subtle bg-card'

/**
 * The three card treatments, chosen by content:
 * - `filled` — the default: data cards, tables, Mitra's note.
 * - `outline` — the hairline in both themes, for a card that sits alone on Home or holds its own
 *   controls (the first-run card, Your portfolio, the ask bar).
 * - `flat` — no box at all, only a hairline above it, for Home's feed (Movers, News):
 *   the feed's own gap sits above the rule and `pt-8` below it, 32 either side. The inset is zero,
 *   so a bleeding row inside it lines up with the column rather than with a card it is not in.
 */
export const sectionCardVariants = cva('text-card-foreground', {
  variants: {
    variant: {
      filled: `${CARD_SURFACE} ${INSET}`,
      outline: `${OUTLINE_SURFACE} ${INSET}`,
      flat: 'border-t border-border-subtle pt-8 [--section-card-pad-x:0rem] [--section-card-pad-y:0rem]',
    },
  },
  defaultVariants: { variant: 'filled' },
})

export type SectionCardVariant = NonNullable<VariantProps<typeof sectionCardVariants>['variant']>

/** The default card, `filled`, as one string for the call sites that paint their own. */
export const SECTION_CARD = sectionCardVariants()
